import React from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';

import Button from '../../components/ui-controls/Button';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {AppRoute} from '../../models/AppRoute';
import {InformationContentType, RouteNavigatorScreenProps} from '../../models/navigationTypes';
import theme from '../../style/theme';

const AccountDeleteInfoScreen = ({
  navigation,
}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_DELETE_INFO>) => {
  const handleGoToStore = () => navigation.navigate(AppRoute.HOME);
  const handleLogin = () => navigation.navigate(AppRoute.ACCOUNT_NOT_LOGGED_IN);
  const handleMyAccount = () => navigation.navigate(AppRoute.ACCOUNT_LOGGED_IN);
  const handleDeleteAccount = () => navigation.navigate(AppRoute.ACCOUNT_DELETE);
  const handlePrivacyPolicy = () =>
    navigation.navigate(AppRoute.INFORMATION_CONTENT, {
      target: InformationContentType.PRIVACY_POLICY,
    });

  return (
    <ContentView testID="account-delete-info-screen">
      <ScrollView showsVerticalScrollIndicator={false}>
        <View style={styles.section}>
          <Text style={styles.heading}>We&apos;re sorry to see you go</Text>
          <Text style={styles.paragraph}>
            Deleting your account will remove your shopping history, wishlist, and communication
            preferences from the app and web store.
          </Text>
          <Text style={styles.paragraph}>
            After you submit this request, your account will be suspended and queued for deletion
            and you will no longer have access or be able to reactivate your account.
          </Text>
          <Text style={styles.paragraph}>
            If you just want to change how often you hear from us you can turn off app notifications
            or adjust your email and SMS communication preferences in your account settings.
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.heading}>What else should you know</Text>
          <Text style={styles.paragraph}>
            Once you delete your account, we will no longer collect or use your personal data. Some
            account information may be retained for fraud, product recall, compliance, regulatory
            and other legal purposes.
          </Text>
          <Text style={styles.paragraph}>
            The full privacy policy is available for review at our{' '}
            <Text style={styles.link} onPress={handlePrivacyPolicy}>
              website
            </Text>{' '}
            (top-left burger menu, Information, Privacy Policy).
          </Text>
          <Text style={styles.paragraph}>
            If you&apos;re sure you&apos;d like to permanently delete your account, please:
          </Text>
          <View style={styles.orderedList}>
            <Text style={styles.listItem}>
              <Text style={styles.listNumber}>1. </Text>
              <Text style={styles.link} onPress={handleLogin}>
                Log in to your account
              </Text>
            </Text>
            <Text style={styles.listItem}>
              <Text style={styles.listNumber}>2. </Text>
              Click on the top-left burger menu and select{' '}
              <Text style={styles.link} onPress={handleMyAccount}>
                &quot;My Account&quot;
              </Text>
            </Text>
            <Text style={styles.listItem}>
              <Text style={styles.listNumber}>3. </Text>
              Click on{' '}
              <Text style={styles.link} onPress={handleDeleteAccount}>
                &quot;Delete My Account&quot;
              </Text>
            </Text>
            <Text style={styles.listItem}>
              <Text style={styles.listNumber}>4. </Text>
              Click on &quot;DELETE MY ACCOUNT&quot; to confirm
            </Text>
          </View>
        </View>
        <View style={styles.buttonsContainer}>
          <Button
            onPress={handleGoToStore}
            title="GO BACK TO THE STORE"
            buttonStyle={styles.button}
          />
        </View>
      </ScrollView>
    </ContentView>
  );
};

const styles = StyleSheet.create({
  heading: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colour.text.heading.primary,
    textAlign: 'center',
    marginBottom: 8,
  },
  paragraph: {
    fontSize: 16,
    color: theme.colour.text.content.primary,
    marginBottom: 8,
  },
  section: {
    marginBottom: 16,
  },
  link: {
    color: theme.colour.primary,
    textDecorationLine: 'underline',
  },
  orderedList: {
    marginTop: 8,
    paddingLeft: 8,
  },
  listItem: {
    fontSize: 16,
    color: theme.colour.text.content.primary,
    marginBottom: 8,
  },
  listNumber: {
    fontWeight: 'bold',
    color: theme.colour.text.content.primary,
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 8,
  },
  button: {
    marginBottom: 8,
    width: 300,
  },
});

export default AccountDeleteInfoScreen;
